// "Go to" keyboard shortcuts (operator request, 23 Oct, after Wiz's
// Go-to menu): press G, then a letter, from anywhere in the shell.
// Two-key sequences (as in Gmail and Linear) leave single letters free
// and never fire while typing in a field. "?" lists every shortcut.
// Pure so the sequence rules are tested (goToShortcuts.test.ts).

export interface GoToTarget {
  key: string;
  label: string;
  /** Route to open; absent for in-shell actions (Apps panel). */
  to?: string;
  action?: 'apps';
}

export const GO_TO: readonly GoToTarget[] = [
  { key: 'h', label: 'Home', to: '/launcher' },
  { key: 'a', label: 'Apps', action: 'apps' },
  { key: 'd', label: 'Dashboards', to: '/dashboards' },
  { key: 'p', label: 'Policies', to: '/policies' },
  { key: 'r', label: 'Reports', to: '/reports' },
  { key: 'c', label: 'Connectors', to: '/integrations/connectors' },
  { key: 's', label: 'Organization settings', to: '/settings' },
];

/**
 * The Go-to keys for this host. On a standalone product's host (MP738)
 * the cross-app destinations (Apps panel, Dashboards, Policies, Reports)
 * are dropped and Home opens the product itself.
 */
export function goToTargetsFor(standaloneEntryRoute: string | null): readonly GoToTarget[] {
  if (!standaloneEntryRoute) return GO_TO;
  return [
    { key: 'h', label: 'Home', to: standaloneEntryRoute },
    ...GO_TO.filter((t) => t.to === '/integrations/connectors' || t.to === '/settings'),
  ];
}

/** How long after G the second key still counts. */
export const GO_TO_WINDOW_MS = 1500;

export type GoToState = { pendingSince: number } | null;

export type GoToStep =
  | { kind: 'ignore' }
  | { kind: 'start' }
  | { kind: 'cancel' }
  | { kind: 'go'; target: GoToTarget };

/**
 * One key press through the G-sequence. `typing` is true when focus is
 * in a field; modifiers always pass through so browser and app
 * shortcuts (Ctrl/⌘ + K, Ctrl/⌘ + /) are untouched.
 */
export function goToStep(
  state: GoToState,
  key: string,
  mods: { ctrl: boolean; meta: boolean; alt: boolean },
  typing: boolean,
  now: number,
  targets: readonly GoToTarget[] = GO_TO,
): GoToStep {
  if (typing || mods.ctrl || mods.meta || mods.alt) return state ? { kind: 'cancel' } : { kind: 'ignore' };
  const k = key.toLowerCase();
  const live = state !== null && now - state.pendingSince <= GO_TO_WINDOW_MS;
  if (live) {
    const target = targets.find((t) => t.key === k);
    return target ? { kind: 'go', target } : { kind: 'cancel' };
  }
  if (k === 'g') return { kind: 'start' };
  return state ? { kind: 'cancel' } : { kind: 'ignore' };
}

export type ShortcutGroups = readonly { title: string; items: readonly { keys: string[]; label: string }[] }[];

/** Every shortcut, for the "?" panel, with this host's Go-to keys. */
export function shortcutGroupsFor(targets: readonly GoToTarget[]): ShortcutGroups {
  return [
  {
    title: 'Anywhere',
    items: [
      { keys: ['⌘/Ctrl', 'K'], label: 'Search pages and dashboards' },
      { keys: ['/'], label: 'Search pages and dashboards' },
      { keys: ['⌘/Ctrl', '/'], label: 'Ask Athena' },
      { keys: ['?'], label: 'Show these shortcuts' },
      { keys: ['Esc'], label: 'Close the open panel or dialog' },
    ],
  },
  {
    title: 'Go to',
    items: targets.map((t) => ({ keys: ['G', t.key.toUpperCase()], label: t.label })),
  },
  ];
}

export const SHORTCUT_GROUPS: ShortcutGroups = shortcutGroupsFor(GO_TO);
